import { padFlatNumber } from "../staffQuarterUtils";
import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { getStaffQuarterAllFlats, createStaffQuarterFlat, getStaffQuarterQuarterTypes, getStaffQuarterLocations, updateFlatPoolType } from "../../../../core/data/redux/staffQuarterSlice";
import { Select } from "antd";
import Table from "../../../../core/common/dataTable/index";
import AddFlatModal from "./AddFlatModal";
import EditFlatModal from "./EditFlatModal";

/* ================= TYPES ================= */
interface FlatRecord {
    key: string;
    location: string;
    block: string;
    flatNo: string;
    type: string;
    pool: string;
    status: "Vacant" | "Occupied" | "Vacant Initiated" | "Maintenance";
    floor?: string;
    area?: string;
}



/* ================= COMPONENTS ================= */
const DashboardTile = ({
    title,
    icon,
    count,
    link,
    colorClass = "text-primary",
}: {
    title: string;
    icon?: string;
    count?: string | number;
    link: string;
    colorClass?: string;
}) => {
    return (
        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-2">
            <div
                className="card h-100 border-0 shadow-sm hover-card"
                style={{
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                }}
            >
                <div className="card-body d-flex align-items-center p-2">
                    <div
                        className={`d-flex align-items-center justify-content-center me-2 rounded`}
                        style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
                            border: "1px solid rgba(0,0,0,0.03)"
                        }}
                    >
                        {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "18px" }}></i>}
                    </div>
                    <div>
                        <div className="text-muted mb-0" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>{title}</div>
                        <div className={`h5 fw-bold mb-0 ${colorClass}`}>{count}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <div className="mb-2 mt-2">
            <div className="row g-2">{children}</div>
        </div>
    );
};

/* ================= MAIN COMPONENT ================= */
const FlatMaster = () => {
    const routes = all_routes;
    const [showAddModal, setShowAddModal] = useState(false);

    const dispatch = useDispatch<any>();
    const [searchParams] = useSearchParams();
    const paramLocation = searchParams.get("location") || "";
    const paramStatus = searchParams.get("status") || "All Statuses";
    const paramBlock = searchParams.get("block") || "";

    const [apiData, setApiData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [backendStats, setBackendStats] = useState({
        total: 0,
        occupied: 0,
        vacant: 0,
        vacationInitiated: 0
    });

    /* ================= STATE & FILTERS ================= */
    const [selectedLocation, setSelectedLocation] = useState(paramLocation);
    const [selectedType, setSelectedType] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(paramStatus);
    const [selectedBlock, setSelectedBlock] = useState(paramBlock);

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedFlatRecord, setSelectedFlatRecord] = useState<any>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Sync filters from URL parameters when they change
    useEffect(() => {
        if (paramLocation !== undefined) setSelectedLocation(paramLocation);
        if (paramStatus !== undefined) setSelectedStatus(paramStatus);
        if (paramBlock !== undefined) setSelectedBlock(paramBlock);
    }, [paramLocation, paramStatus, paramBlock]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        const payload: any = {
            page: currentPage,
            limit: pageSize
        };

        if (selectedLocation) {
            payload.location = selectedLocation;
        }
        if (selectedType && selectedType !== "All Types") {
            payload.flatType = selectedType;
        }
        if (selectedStatus !== "All Statuses") {
            let statusVal = "OCCUPIED";
            if (selectedStatus === "Vacant") statusVal = "VACANT";
            if (selectedStatus === "Vacant Initiated") statusVal = "VACANT_INITIATED";
            if (selectedStatus === "Maintenance") statusVal = "MAINTENANCE";
            payload.status = statusVal;
        }
        if (selectedBlock) {
            payload.block = selectedBlock;
        }

        dispatch(getStaffQuarterAllFlats(payload)).then((res: any) => {
            if (res.payload?.success) {
                const outerData = res.payload.data;

                const statsSource = (outerData && outerData.total !== undefined) ? outerData
                    : (outerData?.data && outerData.data.total !== undefined) ? outerData.data
                        : null;

                if (statsSource) {
                    setBackendStats({
                        total: statsSource.total || 0,
                        occupied: statsSource.occupied || 0,
                        vacant: statsSource.vacant || 0,
                        vacationInitiated: statsSource.vacationInitiated || 0
                    });
                }

                if (Array.isArray(outerData)) {
                    setApiData(outerData);
                    setTotalRecords(outerData.length);
                } else if (outerData && outerData.data && Array.isArray(outerData.data.data)) {
                    setApiData(outerData.data.data);
                    setTotalRecords(outerData.data.total || 0);
                } else if (outerData && Array.isArray(outerData.data)) {
                    setApiData(outerData.data);
                    setTotalRecords(outerData.total || outerData.totalElements || outerData.data.length);
                } else if (outerData && Array.isArray(outerData.flats)) {
                    setApiData(outerData.flats);
                    setTotalRecords(outerData.total || 0);
                } else if (outerData && Array.isArray(outerData.content)) {
                    setApiData(outerData.content);
                    setTotalRecords(outerData.totalElements || 0);
                }
            }
            setIsLoading(false);
        });
    }, [dispatch, currentPage, pageSize, selectedLocation, selectedType, selectedStatus, refreshTrigger]);

    const data: FlatRecord[] = useMemo(() => {
        return apiData.map((item: any) => {
            let statusStr = "Occupied";
            if (item.status === "VACANT") statusStr = "Vacant";
            else if (item.status === "OCCUPIED") statusStr = "Occupied";
            else if (item.status === "VACANT_INITIATED" || item.status === "VACANT INITIATED") statusStr = "Vacant Initiated";
            else statusStr = "Maintenance";

            let floor = "Ground";
            const flatNoStr = item.flatNo?.toString() || "";
            if (flatNoStr.length > 2) {
                const floorNum = parseInt(flatNoStr.slice(0, -2), 10);
                if (floorNum === 1) floor = "1st";
                else if (floorNum === 2) floor = "2nd";
                else if (floorNum === 3) floor = "3rd";
                else floor = `${floorNum}th`;
            } else if (flatNoStr.length === 1) {
                floor = "Ground";
            }

            return {
                key: item.id?.toString() || Math.random().toString(),
                flatId: item.flatId || item.flat_id || item.flatNumber || item.flatNo || item.id?.toString() || "",
                location: item.locationName || item.locationCode || "Unknown",
                block: item.block || "",
                flatNo: item.flatNo || "",
                type: `Type-${item.flatType || ""}`,
                pool: typeof item.pool === 'object' && item.pool ? (item.pool.namePool || item.pool.name || "") : (item.pool || ""),
                poolTypeId: item.poolTypeId || item.pool_type_id || null,
                status: statusStr as any,
                floor: floor,
                area: "N/A"
            };
        });
    }, [apiData]);

    const [dynamicQuarterTypes, setDynamicQuarterTypes] = useState<any[]>([]);
    const [dynamicLocations, setDynamicLocations] = useState<any[]>([]);

    useEffect(() => {
        dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
            if (res.payload?.success) {
                setDynamicQuarterTypes(res.payload.data || []);
            }
        });
        dispatch(getStaffQuarterLocations({})).then((res: any) => {
            if (res.payload?.success) {
                setDynamicLocations(res.payload.data || []);
            }
        });
    }, [dispatch]);

    const blockOptions = useMemo(() => {
        const blocks = new Set(apiData.map(item => item.block).filter(Boolean));
        if (selectedBlock) {
            blocks.add(selectedBlock);
        }
        return Array.from(blocks).sort().map(b => ({ value: b, label: `Tower ${b}` }));
    }, [apiData, selectedBlock]);

    // Dynamic Options for filtering
    const filterOptions = {
        types: [{ id: "", name: "All Types" }, ...dynamicQuarterTypes],
        locations: [{ id: "", code: "", name: "All Locations" }, ...dynamicLocations],
        statuses: ["All Statuses", "Vacant", "Occupied", "Vacant Initiated", "Maintenance"]
    };

    // Data is already filtered by backend, but we map it through FlatRecord mapping
    const filteredData = useMemo(() => {
        let res = data;
        if (selectedBlock) {
            res = res.filter((item: any) =>
                item.block?.toLowerCase() === selectedBlock.toLowerCase() ||
                item.block?.toLowerCase() === `tower ${selectedBlock}`.toLowerCase() ||
                `tower ${item.block}`.toLowerCase() === selectedBlock.toLowerCase()
            );
        }
        return res;
    }, [data, selectedBlock]);

    // Stats Logic (Using backend totals if available, otherwise array length)
    const stats = useMemo(() => ({
        total: backendStats.total || totalRecords || data.length,
        vacant: backendStats.vacant || data.filter(r => r.status === "Vacant").length,
        occupied: backendStats.occupied || data.filter(r => r.status === "Occupied").length,
        vacationInitiated: backendStats.vacationInitiated || data.filter(r => r.status === "Vacant Initiated").length,
    }), [backendStats, data, totalRecords]);



    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case "Vacant": return "badge bg-danger";
            case "Occupied": return "badge bg-success";
            case "Vacant Initiated": return "badge bg-warning text-dark";
            case "Maintenance": return "badge bg-secondary";
            default: return "badge bg-light text-dark border";
        }
    };

    /* ================= CONFIGURATION ================= */
    const dashboardSections = [
        {
            title: "Inventory Overview",
            tiles: [
                { title: "Total Flats", icon: "ti-building", count: stats.total, link: "#", colorClass: "text-primary" },
                { title: "Occupied", icon: "ti-home-check", count: stats.occupied, link: "#", colorClass: "text-success" },
                { title: "Vacant", icon: "ti-home-off", count: stats.vacant, link: "#", colorClass: "text-danger" },
                { title: "Vacation Initiated", icon: "ti-door-exit", count: stats.vacationInitiated, link: "#", colorClass: "text-warning" },
            ]
        }
    ];

    const handleUpdateFlatPool = (flatId: string, poolTypeId: number | null, category?: string) => {
        return dispatch(updateFlatPoolType({
            flat_id: flatId,
            pool_type_id: poolTypeId,
            category: category,
            flat_category: category,
            category_name: category
        })).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200) {
                setRefreshTrigger(prev => prev + 1);
            }
            return res;
        });
    };

    const columns = [
        {
            title: "Location",
            dataIndex: "location",
            className: "fw-bold text-dark",
            sorter: (a: any, b: any) => a.location.localeCompare(b.location)
        },
        {
            title: "Block / Flat",
            dataIndex: "block",
            render: (_: any, record: any) => (
                <span>{record.block}-{padFlatNumber(record.flatNo)} <span className="text-muted small">({record.floor})</span></span>
            )
        },
        {
            title: "Type",
            dataIndex: "type",
            render: (val: any) => <span className="badge bg-light text-dark border">{val}</span>,
            sorter: (a: any, b: any) => a.type.localeCompare(b.type)
        },
        {
            title: "Pool",
            dataIndex: "pool",
            className: "text-muted"
        },
        {
            title: "Status",
            dataIndex: "status",
            className: "text-center",
            render: (val: any) => (
                <span className={getStatusBadgeClass(val)}>{val}</span>
            )
        },
        {
            title: "Action",
            key: "action",
            className: "text-center",
            render: (_: any, record: any) => (
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <button
                        className="btn btn-sm btn-icon btn-light border animate-btn"
                        onClick={() => {
                            setSelectedFlatRecord(record);
                            setShowEditModal(true);
                        }}
                        title="Edit Pool"
                    >
                        <i className="ti ti-edit text-primary"></i>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">Flat Inventory Master</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarter.inventory.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Flat Master</li>
                            </ul>
                        </div>
                        <div className="col-auto float-end ms-auto">
                            <button className="btn btn-primary me-2 shadow-sm" onClick={() => setShowAddModal(true)}>
                                <i className="ti ti-plus me-1"></i> Add New Flat
                            </button>
                            <button className="btn btn-light border shadow-sm">
                                <i className="ti ti-download me-1"></i> Export
                            </button>
                        </div>
                    </div>
                </div>

                {/* DASHBOARD SECTIONS */}
                {dashboardSections.map((section, sIdx) => (
                    <DashboardSection key={sIdx} title={section.title}>
                        {section.tiles.map((tile, tIdx) => (
                            <DashboardTile
                                key={tIdx}
                                title={tile.title}
                                icon={tile.icon}
                                count={tile.count}
                                link={tile.link}
                                colorClass={tile.colorClass}
                            />
                        ))}
                    </DashboardSection>
                ))}

                {/* FILTERS SECTION */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                    <div className="card-body p-3">
                        <div className="d-flex flex-wrap align-items-end gap-3">
                            <div style={{ flex: '1 1 200px' }}>
                                <label className="form-label small text-muted mb-2">Location</label>
                                <Select
                                    className="w-100 custom-select-ui"
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedLocation || ""}
                                    onChange={(value) => {
                                        setSelectedLocation(value);
                                        setCurrentPage(1);
                                    }}
                                    options={filterOptions.locations.map(l => ({ value: l.name === "All Locations" ? "" : l.code, label: l.name }))}
                                    placeholder="Select Location"
                                />
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                                <label className="form-label small text-muted mb-2">Quarter Type</label>
                                <Select
                                    className="w-100 custom-select-ui"
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedType || ""}
                                    onChange={(value) => {
                                        setSelectedType(value);
                                        setCurrentPage(1);
                                    }}
                                    options={filterOptions.types.map(t => ({ value: t.name === "All Types" ? "" : t.name, label: t.name }))}
                                    placeholder="Select Quarter Type"
                                />
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                                <label className="form-label small text-muted mb-2">Block / Tower</label>
                                <Select
                                    className="w-100 custom-select-ui"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    value={selectedBlock || undefined}
                                    onChange={(value) => {
                                        setSelectedBlock(value || "");
                                        setCurrentPage(1);
                                    }}
                                    options={blockOptions}
                                    placeholder="All Blocks"
                                />
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                                <label className="form-label small text-muted mb-2">Status</label>
                                <Select
                                    className="w-100 custom-select-ui"
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedStatus || "All Statuses"}
                                    onChange={(value) => {
                                        setSelectedStatus(value);
                                        setCurrentPage(1);
                                    }}
                                    options={filterOptions.statuses.map(s => ({ value: s, label: s }))}
                                    placeholder="Select Status"
                                />
                            </div>
                            <div className="ms-auto mt-3 mt-md-0 d-flex gap-2">
                                <button
                                    className="btn btn-danger shadow-sm d-inline-flex align-items-center transition-all"
                                    onClick={() => {
                                        setSelectedLocation("");
                                        setSelectedType("");
                                        setSelectedStatus("All Statuses");
                                        setSelectedBlock("");
                                        setCurrentPage(1);
                                    }}
                                >
                                    <i className="ti ti-refresh me-1"></i> Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body p-0">
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            Selection={false}
                            loading={isLoading}
                            paginationConfig={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalRecords,
                            }}
                            handlePagination={(pagination: any) => {
                                setCurrentPage(pagination.current);
                                setPageSize(pagination.pageSize);
                            }}
                            children={<></>}
                        />
                    </div>
                </div>

                {showAddModal && (
                    <AddFlatModal
                        show={showAddModal}
                        onHide={() => setShowAddModal(false)}
                        onAdd={(newFlat: any) => {
                            return dispatch(createStaffQuarterFlat(newFlat)).then((res: any) => {
                                if (res.payload?.success) {
                                    const payload: any = { page: currentPage, limit: pageSize };
                                    if (selectedLocation) payload.location = selectedLocation;
                                    if (selectedType && selectedType !== "All Types") payload.flatType = selectedType;
                                    if (selectedStatus !== "All Statuses") {
                                        payload.status = selectedStatus === "Vacant" ? "VACANT" : selectedStatus === "Occupied" ? "OCCUPIED" : selectedStatus === "Vacant Initiated" ? "VACANT_INITIATED" : "MAINTENANCE";
                                    }
                                    dispatch(getStaffQuarterAllFlats(payload)).then((res2: any) => {
                                        if (res2.payload?.success) {
                                            if (Array.isArray(res2.payload.data)) {
                                                setApiData(res2.payload.data);
                                            } else if (res2.payload.data && Array.isArray(res2.payload.data.data)) {
                                                setApiData(res2.payload.data.data);
                                            } else if (res2.payload.data && Array.isArray(res2.payload.data.flats)) {
                                                setApiData(res2.payload.data.flats);
                                            } else if (res2.payload.data && Array.isArray(res2.payload.data.content)) {
                                                setApiData(res2.payload.data.content);
                                            }
                                        }
                                    });
                                }
                                return res;
                            });
                        }}
                    />
                )}

                {showEditModal && selectedFlatRecord && (
                    <EditFlatModal
                        show={showEditModal}
                        onHide={() => {
                            setShowEditModal(false);
                            setSelectedFlatRecord(null);
                        }}
                        flat={selectedFlatRecord}
                        onUpdate={handleUpdateFlatPool}
                    />
                )}

            </div>
            <style>{`
          .bg-success-light { background-color: rgba(25, 135, 84, 0.1); }
          .bg-danger-light { background-color: rgba(220, 53, 69, 0.1); }
          .bg-info-light { background-color: rgba(13, 202, 240, 0.1); }
          .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.1); }
          .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
          }
          .custom-pagination-default .ant-pagination-options {
            position: static !important;
            margin-left: 16px;
          }
          .custom-pagination-default .ant-pagination-options-size-changer::before,
          .custom-pagination-default .ant-pagination-options-size-changer::after {
            display: none !important;
          }
          .pool-cell-hover .edit-pool-btn {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          .pool-cell-hover:hover .edit-pool-btn {
            opacity: 1;
          }
      `}</style>
        </div>
    );
};

export default FlatMaster;
